// src/data/conditionContent.js
// SEO-rich content for each condition landing page

const conditionContent = [
  {
    slug: "psoriasis",
    name: "Psoriasis",
    title: "Psoriasis Trigger Tracker — Find Your Personal Patterns",
    metaDescription:
      "Track psoriasis flares, food triggers, and symptoms daily with Ninoa. Identify what causes your plaques and predict flare-ups before they happen.",
    intro:
      "Psoriasis is a chronic autoimmune condition that causes red, scaly patches on the skin. Flares can be triggered by stress, diet, weather, and dozens of other factors unique to each person. Ninoa helps you uncover your personal trigger patterns so you can take control of your skin health.",
    howNinoaHelps: [
      "Log plaques, itching, and pain in under 10 seconds each day",
      "Discover which foods, stress levels, and weather patterns trigger your flares",
      "Receive flare predictions based on your personal history and current conditions",
      "Generate dermatologist-ready PDF reports showing your symptom trends over time",
    ],
    commonTriggers: [
      "Alcohol",
      "Dairy",
      "Gluten",
      "Stress",
      "Red meat",
      "Sugar",
      "Nightshades",
      "Cold weather",
      "Processed foods",
    ],
    helperFoods: [
      "Salmon",
      "Turmeric",
      "Leafy greens",
      "Blueberries",
      "Olive oil",
      "Walnuts",
      "Sweet potatoes",
      "Green tea",
    ],
    stats: "125 million people worldwide live with psoriasis",
    ctaText: "Start tracking your psoriasis triggers today",
  },
  {
    slug: "eczema",
    name: "Eczema",
    title: "Eczema Trigger Diary — Track Flares & Find Relief",
    metaDescription:
      "Monitor eczema flares, itchiness, and environmental triggers with Ninoa. Discover your personal allergens and patterns to reduce atopic dermatitis symptoms.",
    intro:
      "Eczema (atopic dermatitis) causes dry, itchy, inflamed skin that can flare unpredictably. Triggers range from allergens and weather to stress and certain foods. Ninoa helps you identify exactly what sets off your eczema so you can avoid flares and find lasting relief.",
    howNinoaHelps: [
      "Track itchiness, dryness, and redness severity with simple daily taps",
      "Identify environmental allergens and food sensitivities triggering your flares",
      "Monitor weather and humidity correlations with your eczema symptoms",
      "Share detailed symptom timelines with your dermatologist via PDF reports",
    ],
    commonTriggers: [
      "Dairy",
      "Eggs",
      "Dust mites",
      "Stress",
      "Soy",
      "Wool",
      "Fragrances",
      "Dry weather",
      "Pollen",
    ],
    helperFoods: [
      "Fatty fish",
      "Probiotics",
      "Flaxseed",
      "Bone broth",
      "Avocado",
      "Spinach",
      "Turmeric",
      "Fermented vegetables",
    ],
    stats: "Over 230 million people globally are affected by eczema",
    ctaText: "Start your eczema trigger diary today",
  },
  {
    slug: "acne",
    name: "Acne",
    title: "Acne Food Tracker — Discover What Triggers Breakouts",
    metaDescription:
      "Track acne breakouts, hormonal patterns, and food triggers with Ninoa. Find the connection between your diet, stress, and skin to clear up faster.",
    intro:
      "Acne affects people of all ages and can be influenced by hormones, diet, stress, and skincare products. Whether you deal with hormonal acne, cystic acne, or adult acne, tracking your daily habits can reveal the patterns behind your breakouts.",
    howNinoaHelps: [
      "Log breakouts by severity and location with quick daily entries",
      "Track hormonal cycle correlations with your acne patterns",
      "Identify which foods (dairy, sugar, processed) trigger new breakouts",
      "Monitor stress levels and sleep quality alongside skin changes",
    ],
    commonTriggers: [
      "Dairy",
      "Sugar",
      "Processed foods",
      "Stress",
      "Hormonal changes",
      "Whey protein",
      "High-glycemic foods",
      "Chocolate",
      "Greasy foods",
    ],
    helperFoods: [
      "Zinc-rich foods",
      "Green tea",
      "Berries",
      "Leafy greens",
      "Probiotics",
      "Omega-3 fish",
      "Nuts and seeds",
      "Whole grains",
    ],
    stats: "Acne affects up to 50 million people in the US alone each year",
    ctaText: "Start tracking your acne triggers today",
  },
  {
    slug: "rosacea",
    name: "Rosacea",
    title: "Rosacea Trigger Tracker — Reduce Redness & Flushing",
    metaDescription:
      "Track rosacea flares, flushing episodes, and personal triggers with Ninoa. Identify foods, weather, and stress patterns that cause redness.",
    intro:
      "Rosacea causes persistent redness, visible blood vessels, and sometimes bumps on the face. Triggers vary widely between individuals but often include spicy food, alcohol, temperature changes, and stress. Understanding your unique triggers is the key to managing rosacea effectively.",
    howNinoaHelps: [
      "Log flushing episodes, redness severity, and bumps daily",
      "Track food and drink triggers like spicy meals, alcohol, and hot beverages",
      "Monitor weather and temperature correlations with your flares",
      "Build a personal trigger profile to share with your dermatologist",
    ],
    commonTriggers: [
      "Spicy food",
      "Alcohol",
      "Hot beverages",
      "Sun exposure",
      "Stress",
      "Wind",
      "Histamine-rich foods",
      "Extreme temperatures",
      "Caffeine",
    ],
    helperFoods: [
      "Cucumber",
      "Oats",
      "Salmon",
      "Leafy greens",
      "Blueberries",
      "Chamomile tea",
      "Ginger",
      "Asparagus",
    ],
    stats: "Over 415 million people worldwide are estimated to have rosacea",
    ctaText: "Start tracking your rosacea triggers today",
  },
  {
    slug: "vitiligo",
    name: "Vitiligo",
    title: "Vitiligo Tracker — Monitor Depigmentation & Progress",
    metaDescription:
      "Track vitiligo progression, repigmentation, and triggers with Ninoa. Monitor sun exposure, stress, and treatment response over time.",
    intro:
      "Vitiligo is an autoimmune condition that causes patches of skin to lose pigment. While the exact causes are still being studied, stress, sun exposure, and certain environmental factors can influence progression. Tracking your symptoms helps you and your doctor make better treatment decisions.",
    howNinoaHelps: [
      "Monitor depigmentation changes and repigmentation progress over time",
      "Track sun exposure, stress, and lifestyle factors that may influence patches",
      "Log treatment responses to help optimize your care plan",
      "Generate visual progress reports to share with your dermatologist",
    ],
    commonTriggers: [
      "Stress",
      "Sunburn",
      "Skin injury",
      "Chemical exposure",
      "Hormonal changes",
      "Lack of sleep",
      "Emotional trauma",
      "Illness",
    ],
    helperFoods: [
      "Leafy greens",
      "Berries",
      "Carrots",
      "Chickpeas",
      "Walnuts",
      "Turmeric",
      "Mushrooms",
      "Green tea",
    ],
    stats: "Vitiligo affects approximately 1% of the world's population",
    ctaText: "Start monitoring your vitiligo journey today",
  },
  {
    slug: "hidradenitis",
    name: "Hidradenitis Suppurativa",
    title: "Hidradenitis Suppurativa Tracker — Manage HS Flares",
    metaDescription:
      "Track hidradenitis suppurativa flares, abscesses, and lifestyle triggers with Ninoa. Identify diet and hormonal patterns to reduce painful HS episodes.",
    intro:
      "Hidradenitis suppurativa (HS) causes painful lumps, abscesses, and tunnels under the skin, typically in areas where skin rubs together. Diet, hormones, and stress are common triggers. Consistent tracking helps identify patterns and gives your care team actionable data.",
    howNinoaHelps: [
      "Log abscess location, pain level, and drainage with quick daily entries",
      "Track dietary triggers including dairy, sugar, and nightshades",
      "Monitor hormonal patterns and stress correlations with HS flares",
      "Create detailed flare timelines for your dermatologist or surgeon",
    ],
    commonTriggers: [
      "Dairy",
      "Sugar",
      "Nightshades",
      "Stress",
      "Hormonal changes",
      "Smoking",
      "Tight clothing",
      "Heat and humidity",
      "Yeast",
    ],
    helperFoods: [
      "Anti-inflammatory fish",
      "Turmeric",
      "Leafy greens",
      "Berries",
      "Zinc-rich foods",
      "Sweet potatoes",
      "Garlic",
      "Green tea",
    ],
    stats: "HS affects up to 4% of the population, often misdiagnosed for years",
    ctaText: "Start tracking your HS triggers today",
  },
  {
    slug: "alopecia",
    name: "Alopecia Areata",
    title: "Alopecia Tracker — Monitor Hair Loss & Regrowth",
    metaDescription:
      "Track alopecia areata hair loss patterns, stress triggers, and treatment progress with Ninoa. Monitor regrowth and share reports with your doctor.",
    intro:
      "Alopecia areata is an autoimmune condition that causes sudden hair loss in patches. Stress, illness, and genetic factors all play a role. Tracking your hair loss and regrowth patterns over time helps you understand your triggers and measure treatment effectiveness.",
    howNinoaHelps: [
      "Log hair loss episodes and regrowth progress with daily tracking",
      "Identify stress, diet, and seasonal patterns that correlate with flares",
      "Monitor treatment response and recovery timelines",
      "Generate progress reports to share with your dermatologist",
    ],
    commonTriggers: [
      "Stress",
      "Illness",
      "Hormonal changes",
      "Iron deficiency",
      "Vitamin D deficiency",
      "Emotional trauma",
      "Autoimmune flares",
      "Seasonal changes",
    ],
    helperFoods: [
      "Iron-rich foods",
      "Eggs",
      "Spinach",
      "Salmon",
      "Nuts and seeds",
      "Sweet potatoes",
      "Lentils",
      "Avocado",
    ],
    stats: "Alopecia areata affects roughly 6.8 million people in the US",
    ctaText: "Start tracking your alopecia patterns today",
  },
  {
    slug: "urticaria",
    name: "Urticaria (Hives)",
    title: "Hives Tracker — Log Outbreaks & Find Histamine Triggers",
    metaDescription:
      "Track chronic urticaria and hive outbreaks with Ninoa. Identify histamine triggers, food sensitivities, and stress patterns causing your hives.",
    intro:
      "Urticaria (hives) causes itchy welts that can appear suddenly and last for hours or days. Chronic urticaria can persist for months, driven by histamine responses to food, stress, medications, and environmental factors. Tracking helps you find your personal triggers and reduce outbreaks.",
    howNinoaHelps: [
      "Log hive outbreaks with severity, location, and duration",
      "Track histamine-rich foods and identify dietary triggers",
      "Monitor medication, stress, and environmental factor correlations",
      "Build a comprehensive trigger profile for your allergist or dermatologist",
    ],
    commonTriggers: [
      "Histamine-rich foods",
      "Shellfish",
      "Nuts",
      "Stress",
      "NSAIDs",
      "Alcohol",
      "Food additives",
      "Temperature changes",
      "Tight clothing",
    ],
    helperFoods: [
      "Fresh vegetables",
      "Fresh meat (not aged)",
      "Rice",
      "Apples",
      "Pears",
      "Quercetin-rich foods",
      "Ginger",
      "Fresh herbs",
    ],
    stats: "Up to 20% of people experience hives at some point in their lives",
    ctaText: "Start tracking your hive outbreaks today",
  },
  {
    slug: "perioral-dermatitis",
    name: "Perioral Dermatitis",
    title: "Perioral Dermatitis Tracker — Identify Product Triggers",
    metaDescription:
      "Track perioral dermatitis flares, product reactions, and healing progress with Ninoa. Identify toothpaste, skincare, and steroid triggers causing your rash.",
    intro:
      "Perioral dermatitis causes a bumpy, sometimes scaly rash around the mouth, nose, or eyes. It is often triggered by topical steroids, fluoride toothpaste, heavy skincare products, and hormonal changes. Tracking your products and flares helps pinpoint exactly what is causing your symptoms.",
    howNinoaHelps: [
      "Log rash severity and location with simple daily entries",
      "Track skincare products, toothpaste, and makeup to identify irritants",
      "Monitor steroid use and rebound flares during treatment withdrawal",
      "Share your trigger timeline with your dermatologist for faster diagnosis",
    ],
    commonTriggers: [
      "Topical steroids",
      "Fluoride toothpaste",
      "Heavy moisturizers",
      "SLS in products",
      "Hormonal changes",
      "Sunscreen ingredients",
      "Stress",
      "Wind and cold",
    ],
    helperFoods: [
      "Probiotics",
      "Zinc-rich foods",
      "Leafy greens",
      "Berries",
      "Omega-3 fish",
      "Turmeric",
      "Fermented foods",
      "Bone broth",
    ],
    stats: "Perioral dermatitis affects up to 1% of the population, mostly women aged 20-45",
    ctaText: "Start tracking your perioral dermatitis today",
  },
  {
    slug: "seborrheic-dermatitis",
    name: "Seborrheic Dermatitis",
    title: "Seborrheic Dermatitis Tracker — Manage Flaking & Flares",
    metaDescription:
      "Track seborrheic dermatitis flares, scalp health, and seasonal patterns with Ninoa. Identify triggers causing dandruff, redness, and flaking.",
    intro:
      "Seborrheic dermatitis causes flaky, scaly patches on the scalp, face, and chest. It is linked to an overgrowth of Malassezia yeast and can be worsened by stress, cold weather, and certain foods. Tracking your symptoms and lifestyle helps you find what keeps your skin balanced.",
    howNinoaHelps: [
      "Log flaking, redness, and itching severity across affected areas",
      "Track seasonal and weather patterns that worsen your symptoms",
      "Identify dietary triggers and stress correlations with your flares",
      "Generate symptom trend reports for more productive doctor visits",
    ],
    commonTriggers: [
      "Stress",
      "Cold dry weather",
      "Alcohol",
      "Sugar",
      "Yeast-containing foods",
      "Heavy hair products",
      "Lack of sleep",
      "Hormonal changes",
      "Hot showers",
    ],
    helperFoods: [
      "Probiotics",
      "Garlic",
      "Coconut oil",
      "Leafy greens",
      "Omega-3 fish",
      "Zinc-rich foods",
      "Biotin-rich foods",
      "Apple cider vinegar",
    ],
    stats: "Seborrheic dermatitis affects 3-5% of the global population",
    ctaText: "Start tracking your seborrheic dermatitis today",
  },
  {
    slug: "prurigo-nodularis",
    name: "Prurigo Nodularis",
    title: "Prurigo Nodularis Tracker — Track Itch, Sleep & Nodules",
    metaDescription:
      "Track prurigo nodularis itch intensity, nodule count, sleep loss, and triggers with Ninoa. Build the itch record your dermatologist needs for treatment decisions.",
    intro:
      "Prurigo nodularis causes firm, intensely itchy nodules, most often on the arms, legs, and upper back. The itch comes first and the nodules form from persistent scratching, which makes breaking the itch-scratch cycle the whole goal of treatment. Tracking your itch intensity, sleep loss, and triggers turns a year of suffering into a record your dermatologist can act on.",
    howNinoaHelps: [
      "Rate your worst itch 0 to 10 daily, the same scale used in prurigo nodularis research",
      "Log nodule locations, sleep loss, and scratching so patterns become visible",
      "Track heat, sweat, stress, and fabrics against your itch to find your personal triggers",
      "Generate a doctor-ready PDF showing itch scores and disrupted nights over time",
    ],
    commonTriggers: [
      "Heat",
      "Sweating",
      "Stress",
      "Wool and rough fabrics",
      "Dry skin",
      "Hot showers",
      "Poor sleep",
      "Cold dry weather",
      "Insect bites",
      "Alcohol",
    ],
    helperFoods: [
      "Omega-3 fish",
      "Leafy greens",
      "Turmeric",
      "Berries",
      "Olive oil",
      "Apples and pears",
      "Oats",
      "Probiotics",
    ],
    stats: "Prurigo nodularis carries one of the heaviest itch and sleep burdens in dermatology",
    ctaText: "Start tracking your prurigo nodularis today",
  },
];

export default conditionContent;
